import React, { useState, useRef, useMemo } from 'react'
import type { WeekViewProps, CalendarEvent } from '../types'
import { AppointmentCard } from '../components/AppointmentCard'
import { TimeLabels } from '../components/TimeLabels'
import { CurrentTimeIndicator } from '../components/CurrentTimeIndicator'
import {
  getWeekDays,
  getEventsForDay,
  isToday,
  generateTimeSlots,
} from '../utils/time'
import {
  calculateOverlapLayout,
  getOverlapEventStyle,
  DEFAULT_SLOT_HEIGHT,
} from '../utils/positioning'
import { getDayNameShort } from '../utils/locale'

export const WeekView: React.FC<WeekViewProps> = ({
  weekStart,
  events,
  slotDuration,
  startHour,
  endHour,
  editable,
  locale,
  selectedEventIds = [],
  onEventClick,
  onSlotClick,
  onEventDrop,
}) => {
  const [draggedEvent, setDraggedEvent] = useState<CalendarEvent | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  const weekDays = getWeekDays(weekStart)
  const slots = generateTimeSlots(startHour, endHour, slotDuration)
  const slotHeight = DEFAULT_SLOT_HEIGHT
  const totalSlots = ((endHour - startHour) * 60) / slotDuration
  const gridHeight = totalSlots * slotHeight

  const handleDragStart = (event: CalendarEvent) => (e: React.DragEvent) => {
    setDraggedEvent(event)
    e.dataTransfer.effectAllowed = 'move'
  }

  const handleDrop = (e: React.DragEvent, day: Date, hour: number, minute: number) => {
    e.preventDefault()
    if (draggedEvent && editable) {
      const newStart = new Date(day)
      newStart.setHours(hour, minute, 0, 0)
      onEventDrop?.(draggedEvent, newStart)
      setDraggedEvent(null)
    }
  }

  const handleSlotClick = (day: Date, hour: number, minute: number) => {
    if (editable) {
      onSlotClick?.(day, hour, minute)
    }
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
  }

  return (
    <div className="calendar-week-view">
      {/* Week Header - Day Names */}
      <div
        style={{
          display: 'flex',
          borderBottom: '1px solid #e5e7eb',
          backgroundColor: '#f9fafb',
        }}
      >
        {/* Empty corner for time labels */}
        <div style={{ width: '60px', flexShrink: 0 }} />

        {/* Day headers */}
        {weekDays.map((day) => {
          const dayIsToday = isToday(day)

          return (
            <div
              key={day.toISOString()}
              style={{
                flex: 1,
                textAlign: 'center',
                padding: '12px 4px',
                backgroundColor: dayIsToday ? '#fef3c7' : undefined,
                borderLeft: '1px solid #e5e7eb',
              }}
            >
              <div
                style={{
                  fontSize: '12px',
                  fontWeight: 500,
                  color: '#6b7280',
                  textTransform: 'uppercase',
                }}
              >
                {getDayNameShort(day.getDay(), locale)}
              </div>
              <div
                style={{
                  fontSize: '18px',
                  fontWeight: dayIsToday ? 700 : 500,
                  color: dayIsToday ? '#1e3a5f' : '#111827',
                  marginTop: '2px',
                }}
              >
                {day.getDate()}
              </div>
            </div>
          )
        })}
      </div>

      {/* Time Grid Container */}
      <div
        ref={containerRef}
        style={{
          display: 'flex',
          maxHeight: '600px',
          overflowY: 'auto',
          overflowX: 'hidden',
        }}
      >
        {/* Time Labels */}
        <TimeLabels
          startHour={startHour}
          endHour={endHour}
          slotDuration={slotDuration}
          slotHeight={slotHeight}
        />

        {/* Day Columns */}
        <div style={{ display: 'flex', flex: 1 }}>
          {weekDays.map((day) => {
            const dayEvents = getEventsForDay(events, day)
            const dayIsToday = isToday(day)

            // Calculate overlap layout for this day's events
            const eventLayouts = useMemo(
              () => calculateOverlapLayout(dayEvents, startHour, slotDuration, slotHeight),
              [dayEvents, startHour, slotDuration, slotHeight]
            )

            return (
              <div
                key={day.toISOString()}
                style={{
                  flex: 1,
                  position: 'relative',
                  borderLeft: '1px solid #e5e7eb',
                  backgroundColor: dayIsToday ? '#fffef0' : undefined,
                  minHeight: gridHeight,
                }}
              >
                {/* Time Slots */}
                {slots.map((slot) => {
                  const isHourStart = slot.minute === 0

                  return (
                    <div
                      key={`${day.toISOString()}-${slot.hour}-${slot.minute}`}
                      style={{
                        height: slotHeight,
                        borderBottom: isHourStart
                          ? '1px solid #d1d5db'
                          : '1px dashed #e5e7eb',
                        cursor: editable ? 'pointer' : 'default',
                        boxSizing: 'border-box',
                      }}
                      onClick={() => handleSlotClick(day, slot.hour, slot.minute)}
                      onDragOver={handleDragOver}
                      onDrop={(e) => handleDrop(e, day, slot.hour, slot.minute)}
                      className="calendar-time-slot"
                    />
                  )
                })}

                {/* Events Overlay with Overlap Handling */}
                {eventLayouts.map((layout) => {
                  const style = getOverlapEventStyle(layout)
                  const isSelected = selectedEventIds.includes(layout.event.id)

                  return (
                    <div
                      key={layout.event.id}
                      style={{
                        position: 'absolute',
                        top: style.top + 1,
                        left: style.left,
                        width: style.width,
                        height: style.height - 2,
                        zIndex: isSelected ? 2 : 1,
                        paddingLeft: '4px',
                        paddingRight: layout.totalColumns > 1 ? '2px' : '4px',
                      }}
                      onClick={(e) => {
                        const modifiers = {
                          ctrl: e.ctrlKey || e.metaKey,
                          shift: e.shiftKey,
                        }
                        onEventClick?.(layout.event, modifiers)
                      }}
                    >
                      <AppointmentCard
                        event={layout.event}
                        locale={locale}
                        compact={style.height < 60}
                        draggable={editable}
                        selected={isSelected}
                        onDragStart={handleDragStart(layout.event)}
                      />
                    </div>
                  )
                })}

                {/* Current Time Indicator */}
                {dayIsToday && (
                  <CurrentTimeIndicator
                    startHour={startHour}
                    slotDuration={slotDuration}
                    slotHeight={slotHeight}
                  />
                )}
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
